import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";
import { SortDefinition } from "src/uniform/contenttypes/sortDefinition";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";

import { countActiveFilterOptions } from "../utils/helpers";

import { FILTER_DROPDOWN_DATA_ATTR, FilterControl } from "./filter-control";
import { SearchField } from "./search-field";
import { SortControl } from "./sort-control";

import type { UniformFilterObject } from "../utils/types";

interface ToolbarProps {
    readonly filterDefs: FilterDefinition[];
    readonly sortDefs: SortDefinition[];
    readonly canSearch: boolean;
    readonly showSearchInput: boolean;
    readonly filterObject: UniformFilterObject;
    readonly searchQuery: string;
    readonly selectedSortId: string | undefined;
    readonly dropdownVisibility: Record<string, boolean>;
    readonly labelMaps: Record<string, Record<string, string>>;
    readonly onSearchChange: (value: string) => void;
    readonly onSearchSubmit: () => void;
    readonly onSearchClear: () => void;
    readonly onFilterButtonClick: (filterId: string) => void;
    readonly onOptionToggle: (filterId: string, optionId: string) => void;
    readonly onClearAllFilters: () => void;
    readonly onSortChange: (sortId: string) => void;
}

/**
 * Top row of the filterable-gallery surface: search field, the row of filter
 * controls (with a Clear-Filters action), and the sort control. Each section
 * is hidden when its corresponding definition list is empty.
 */
export const Toolbar = ({
    filterDefs,
    sortDefs,
    canSearch,
    showSearchInput,
    filterObject,
    searchQuery,
    selectedSortId,
    dropdownVisibility,
    labelMaps,
    onSearchChange,
    onSearchSubmit,
    onSearchClear,
    onFilterButtonClick,
    onOptionToggle,
    onClearAllFilters,
    onSortChange
}: ToolbarProps) => {
    const intl = useIntl();

    return (
        <section css={toolbarSectionStyle}>
            <div css={toolbarRowStyle}>
                {showSearchInput && canSearch && (
                    <div css={searchContainerStyle}>
                        <SearchField
                            value={searchQuery}
                            onChange={onSearchChange}
                            onSubmit={onSearchSubmit}
                            onClear={onSearchClear}
                        />
                    </div>
                )}

                {filterDefs.length > 0 && (
                    <div css={filterButtonRowStyle}>
                        {filterDefs.map((filter) => (
                            <div
                                key={filter.id}
                                {...{ [FILTER_DROPDOWN_DATA_ATTR]: "" }}
                            >
                                <FilterControl
                                    filter={filter}
                                    activeCount={countActiveFilterOptions(
                                        filter.id,
                                        filterObject
                                    )}
                                    isOpen={!!dropdownVisibility[filter.id]}
                                    subFilter={filterObject[filter.id]}
                                    labelMap={labelMaps[filter.id]}
                                    onButtonClick={() =>
                                        onFilterButtonClick(filter.id)
                                    }
                                    onOptionToggle={(optionId) =>
                                        onOptionToggle(filter.id, optionId)
                                    }
                                />
                            </div>
                        ))}

                        <Button
                            variant="primary_alt"
                            size="small"
                            onClick={onClearAllFilters}
                            className="body-md"
                            css={clearFiltersButtonStyle}
                            dataEventProps={{
                                "data-product-key": "uniform",
                                "data-event": "clicked",
                                "data-event-component": "button",
                                "data-event-container": "searchWithFilters",
                                "data-label": "Clear Filters",
                                "data-label-english": "Clear Filters"
                            }}
                        >
                            {intl.formatMessage({
                                id: "uniformFilterableGallery.clearFilters",
                                defaultMessage: "Clear Filters"
                            })}
                        </Button>
                    </div>
                )}

                {sortDefs.length > 0 && (
                    <div css={sortRowStyle}>
                        <SortControl
                            sorts={sortDefs}
                            selectedSortId={selectedSortId}
                            onChange={onSortChange}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

const toolbarSectionStyle = css({
    display: "flex",
    flexDirection: "column",
    position: "relative"
});

const toolbarRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    [media.above.sm]: {
        flexDirection: "row",
        alignItems: "center"
    }
});

const searchContainerStyle = css({
    flex: "1 1 auto",
    minWidth: "200px"
});

const filterButtonRowStyle = css({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: token("space.100"),
    position: "relative"
});

const clearFiltersButtonStyle = css({
    whiteSpace: "nowrap"
});

const sortRowStyle = css({
    marginBlockStart: token("space.150"),
    display: "flex",
    justifyContent: "flex-end"
});
